import { useCallback, useMemo } from "react";
import { type Components, Virtuoso, type VirtuosoProps } from "react-virtuoso";

import { BTText } from "@btnet/core-components/BTText";
import type { EntityNotificationsModel } from "@btnet/services-btnet/handlers/models/entityNotificationsModel";
import type { NotificationModel } from "@btnet/services-btnet/handlers/models/notificationModel";

import { BdsIconNotifications } from "@buildertrend/components";

import {
    NotificationCard,
    NotificationCardSkeleton,
} from "entity/notification/panel/NotificationCard";

import "./NotificationCardList.less";

interface INotificationVirtuosoContext {
    hasMore: boolean;
    isLoadingMore: boolean;
}

function NotificationListVirtuosoFooter({ context }: { context?: INotificationVirtuosoContext }) {
    if (context?.isLoadingMore) {
        return (
            <>
                <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
                <div className="NotificationCardListItemDivider" />
                <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
                <div className="NotificationCardListItemDivider" />
                <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
                <div className="NotificationCardListItemDivider" />
                <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
            </>
        );
    }

    if (context?.hasMore) {
        return null;
    }

    return (
        <div className="flex flex-col py-2 gap-1 justify-center items-center text-center">
            <BdsIconNotifications size="lg" />
            <BTText
                className="NotificationListEmptyStateText"
                text="You’re all caught up"
                size="distinct-md"
            />
        </div>
    );
}

const virtuosoComponents: Components<EntityNotificationsModel, INotificationVirtuosoContext> = {
    Footer: NotificationListVirtuosoFooter,
};

interface INotificationCardListProps {
    notificationsByEntitiesList: EntityNotificationsModel[];
    selectedJobId: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    emptyStateComponent: React.ReactNode;

    onLoadMore: () => void;
    onNotificationMarkedRead: (notification: NotificationModel, isRead: boolean) => Promise<void>;
    onNotificationClicked: (notification: NotificationModel) => Promise<void>;
    onNotificationUnfollowed: (notification: NotificationModel) => Promise<void>;
}

export function NotificationCardList({
    notificationsByEntitiesList,
    selectedJobId,
    hasMore,
    isLoadingMore,
    emptyStateComponent,
    onLoadMore,
    onNotificationClicked,
    onNotificationMarkedRead,
    onNotificationUnfollowed,
}: INotificationCardListProps) {
    const context: INotificationVirtuosoContext = useMemo(
        () => ({
            hasMore,
            isLoadingMore,
        }),
        [hasMore, isLoadingMore],
    );

    const renderCard: VirtuosoProps<
        EntityNotificationsModel,
        INotificationVirtuosoContext
    >["itemContent"] = useCallback(
        (index, entityNotifications) => (
            <>
                <NotificationCard
                    key={entityNotifications.entityId}
                    className="NotificationCardListItemWrapper"
                    entityNotifications={entityNotifications}
                    selectedJobId={selectedJobId}
                    onNotificationMarkedRead={onNotificationMarkedRead}
                    onNotificationClicked={onNotificationClicked}
                    onNotificationUnfollowed={onNotificationUnfollowed}
                />
                {index < notificationsByEntitiesList.length - 1 && (
                    <div className="NotificationCardListItemDivider" />
                )}
            </>
        ),
        [
            notificationsByEntitiesList.length,
            onNotificationClicked,
            onNotificationMarkedRead,
            onNotificationUnfollowed,
            selectedJobId,
        ],
    );

    return (
        <div className="NotificationCardList">
            {notificationsByEntitiesList.length === 0 && emptyStateComponent}
            {notificationsByEntitiesList.length !== 0 && (
                <Virtuoso
                    className="VirtuosoOverflowAnchorFix"
                    data={notificationsByEntitiesList}
                    endReached={hasMore && !isLoadingMore ? onLoadMore : undefined}
                    components={virtuosoComponents}
                    increaseViewportBy={400}
                    context={context}
                    style={{ overflowX: "hidden" }}
                    itemContent={renderCard}
                />
            )}
        </div>
    );
}

export function NotificationCardListSkeleton() {
    return (
        <div className="NotificationCardList">
            <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
            <div className="NotificationCardListItemDivider" />
            <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
            <div className="NotificationCardListItemDivider" />
            <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
            <div className="NotificationCardListItemDivider" />
            <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
            <div className="NotificationCardListItemDivider" />
            <NotificationCardSkeleton className="NotificationCardListItemWrapper" />
        </div>
    );
}
